// A single source for code boxes, option columns, examples and catalog numbers.
// The legacy tables stay in saved data; this adapter does not mutate old sheets.
const clean = v => String(v ?? '').trim();
const norm = v => clean(v).toLowerCase().replace(/[^a-z0-9]/g, '');
export const offered = c => c.enabled === false ? [] : (c.options || []).filter(o => o.enabled !== false && (clean(o.code) || clean(o.label)));
export const selectedCode = c => offered(c).find(o => clean(o.code) === clean(c.selected))?.code ?? offered(c)[0]?.code ?? '';
export const groupCode = g => g.prefix != null ? clean(g.prefix) : (g.columns || []).map(selectedCode).filter(Boolean).join(g.sep || '');

const COLUMN_ALIASES = {
  M: ['mounting'], F: ['family'], Z: ['size'], P: ['shape'], B: ['bafflestyle'], T: ['flangestyle'], E: ['mechanism'],
  W: ['wattage', 'wattageunpadded'], CR: ['cri'], CT: ['colourtemperature', 'colortemperature'], BM: ['beamspread'],
  F1: ['flangefinish', 'housingfinish'], F2: ['bafflefinish'], A: ['baffleaccessory', 'additionallisting', 'addllisting'],
  D: ['dimming', 'dimmingoptional'], V: ['voltage'], Y: ['housingstyle'], C: ['collar'], AP: ['aperture', 'aperturesize'], SH: ['apertureshape'], AD: ['adaptersetsdimming'],
};
const patterns = [
  ['mounting', ['M']], ['familysizeshape', ['F', 'Z', 'P']], ['familysize', ['F', 'Z']],
  ['baffleflangestyle', ['B', 'T']], ['mechanism', ['E']], ['wattage', ['W']], ['cricolourtemp', ['CR', 'CT']],
  ['beamspread', ['BM']], ['flangebafflefinish', ['F1', 'F2']], ['housingbafflefinish', ['F1', 'F2']],
  ['baffleaccessory', ['A']], ['addllisting', ['A']], ['housingstyle', ['Y']], ['collar', ['C']],
  ['dimming', ['D']], ['voltage', ['V']], ['adapter', ['AD']],
];

export function orderingGroups(ordering = {}, kind = 'fixture') {
  if (Array.isArray(ordering.groups)) return ordering.groups;
  const cols = (ordering.tables || []).flatMap(t => t.columns || []);
  const column = key => {
    const aliases = COLUMN_ALIASES[key] || [key.toLowerCase()];
    const found = cols.find(c => c.key === key || aliases.includes(norm(c.title)));
    return found ? { ...found, id: key, options: (found.options || []).map(o => ({ ...o })) } : { id: key, title: key, options: [] };
  };
  const groups = (ordering.segments || []).map((s, i) => {
    const label = norm(s.label);
    if (kind === 'housing' && (label === '' || label === 'leds' || label === 'numberofleds' || label === 'lamps')) return null;
    if (kind === 'housing' && label.startsWith('styledimensions')) return { id: 'style', label: 'HOUSING STYLE', columns: [column('Y')] };
    if (label === 'series') return { id: 'series', label: s.label, prefix: s.code, decode: s.code === 'H' ? 'H Housing' : 'T Track', columns: [] };
    if (label.startsWith('aperture')) {
      const c = column('AP');
      return { id: 'aperture', label: s.label, columns: c.options.length ? [c] : [{ id: 'AP', title: 'APERTURE', options: [{ code: s.code, label: '' }] }] };
    }
    const keys = patterns.find(([p]) => p === label)?.[1];
    const columns = keys ? keys.map(column) : [cols.find(c => norm(c.title) === label) || { title: s.label, options: [{ code: s.code, label: 'Confirm field options' }] }];
    return { id: 'legacy-' + i, label: s.label, columns };
  }).filter(Boolean);
  if (kind === 'housing') {
    const style = groups.findIndex(g => g.id === 'style');
    if (style >= 0 && !groups.some(g => norm(g.label) === 'collar')) groups.splice(style + 1, 0, { id: 'collar', label: 'COLLAR', columns: [column('C')] });
  }
  return restoreLegacySelections(groups, ordering.example, kind);
}

function restoreLegacySelections(groups, example, kind) {
  const parts = clean(example).split('-');
  if (parts.length < 2) return groups;
  let housing = null;
  if (kind === 'housing') {
    // Old housing codes combine style/dimension/collar/lamp count. Remove
    // only the explicitly retired fields, keeping the chosen style/collar.
    const combined = /^(IH|OH|RH|VH)[A-F]?([DTW])\d+$/.exec(parts[2] || '');
    if (combined) housing = { series: parts[0], aperture: parts[1], housingstyle: combined[1], collar: combined[2], wattage: parts[3], dimming: parts[4], voltage: parts[5], addllisting: parts[6] };
    else if (parts.length >= 8) housing = { series: parts[0], aperture: parts[1], housingstyle: parts[2], collar: parts[3], wattage: parts[parts.length - 4], dimming: parts[parts.length - 3], voltage: parts[parts.length - 2], addllisting: parts[parts.length - 1] };
  }
  return groups.map((g, i) => {
    const code = clean(housing ? housing[norm(g.label)] : parts[i]);
    if (!code) return g;
    if (g.prefix != null || (kind === 'housing' && g.id === 'aperture')) return { ...g, prefix: code, decode: g.id === 'aperture' ? `${code} Aperture series` : g.decode, columns: [] };
    let remaining = code;
    const columns = (g.columns || []).map(c => {
      const match = [...offered(c)].sort((a, b) => clean(b.code).length - clean(a.code).length).find(o => remaining.startsWith(clean(o.code)))
        || (c.id === 'W' ? offered(c).find(o => Number(o.code) === Number(remaining)) : null);
      if (match) {
        remaining = remaining.slice(clean(match.code).length);
        const fixedVariant = ['M', 'F', 'Z', 'P', 'B', 'E'].includes(c.id) && kind === 'fixture';
        return { ...c, selected: match.code, options: fixedVariant ? c.options.map(o => ({ ...o, enabled: o.code === match.code })) : c.options };
      }
      if ((g.columns || []).length === 1) {
        remaining = '';
        return { ...c, selected: code, options: [...(c.options || []), { code, label: 'Saved value — confirm meaning' }] };
      }
      return c;
    });
    // Do not silently substitute first-option values for an unparseable old code.
    if (remaining) return { ...g, columns: [{ id: 'saved-' + i, title: g.label, selected: code, options: [{ code, label: 'Saved value — confirm field mapping' }] }] };
    return { ...g, columns };
  });
}

export function mergePrefix(groups) {
  let count = 0;
  for (const g of groups) {
    const leading = /^(mounting|familysize(shape)?|series|aperture.*|prefix)$/.test(norm(g.label));
    if (!leading || !(g.prefix != null || (g.columns?.length && g.columns.every(c => offered(c).length === 1)))) break;
    count += 1;
  }
  if (count < 2) return groups;
  const prefix = groups.slice(0, count);
  return [{ id: 'prefix', label: 'PREFIX', prefix: prefix.map(groupCode).join('-'), decode: prefix.map(g => g.decode || (g.columns || []).map(c => offered(c).map(o => `${o.code} ${o.label || ''}`).join(' · ')).join(' · ')).join(' · '), columns: [], sourceGroups: prefix }, ...groups.slice(count)];
}

export function visibleOrderingGroups(o, kind = 'fixture') {
  return orderingGroups(o, kind).filter(g => g.enabled !== false).map(g => ({ ...g, columns: (g.columns || []).filter(c => offered(c).length) })).filter(g => g.prefix != null ? clean(g.prefix) : g.columns.length);
}

export function catalogNumberFor(data, kind = 'fixture') {
  return typeof data.catalogNumberOverride === 'string' ? data.catalogNumberOverride : catalogFor(data.ordering, kind);
}

export function catalogFor(o, kind = 'fixture', overrides = {}) {
  return mergePrefix(visibleOrderingGroups(o, kind)).map(g => {
    if (g.prefix != null) return groupCode(g);
    return (g.columns || []).map(c => overrides[c.id] ?? selectedCode(c)).filter(Boolean).join(g.sep || '');
  }).filter(Boolean).join('-');
}

export function wattageOptions(o) {
  return visibleOrderingGroups(o).flatMap(g => g.columns || []).filter(c => c.id === 'W' || norm(c.title).startsWith('wattage')).flatMap(offered);
}

export function lumenTableFor(f) {
  const table = f.photometrics.lumenTable;
  const options = wattageOptions(f.ordering);
  if (!options.length) return { ...table, rows: [] };
  const watts = value => { const match = clean(value).match(/(\d+(?:\.\d+)?)\s*w\b/i); return match ? Number(match[1]) : Number(value); };
  const source = table.rows || [];
  return { ...table, rows: options.map(o => {
    const old = source.find(r => watts(r[1]) === watts(o.label) || watts(r[1]) === Number(o.code));
    return [catalogFor(f.ordering, 'fixture', { W: o.code }), o.label || `${Number(o.code)} W`, ...(old ? old.slice(2) : ['—', '—'])];
  }) };
}